import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from 'next-themes';
import { toast } from 'sonner';
import { Moon, Sun, Camera } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton, Spinner } from '@/components/ui/misc';
import { InitialsAvatar } from '@/components/InitialsAvatar';
import { PhoneInput } from '@/components/PhoneInput';
import { NotificationPrefs } from '@/components/NotificationPrefs';
import { apiBase, makeClient, uploadAvatar } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { useT } from '@/lib/i18n';

export function SettingsPage() {
  const t = useT();
  const qc = useQueryClient();
  const { data: me, isLoading } = useMe(true);
  const isAdmin = me?.role === 'admin' || me?.role === 'super_admin';
  const { resolvedTheme, setTheme } = useTheme();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Inicializa o form com o perfil quando carrega.
  useEffect(() => {
    if (me) { setName(me.name ?? ''); setPhone(me.phone ?? ''); setJobTitle(me.job_title ?? ''); }
  }, [me?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const { error } = await makeClient().PATCH('/me', { body: { name, phone, job_title: jobTitle } });
      if (error) { toast.error(t('settings.saveFailed')); return; }
      qc.invalidateQueries({ queryKey: ['me'] });
      toast.success(t('settings.saved'));
    } finally { setBusy(false); }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await uploadAvatar(file);
      qc.invalidateQueries({ queryKey: ['me'] });
      toast.success(t('settings.avatarUpdated'));
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      toast.error(code === 'uploads_disabled' ? t('settings.uploadsDisabled') : t('settings.avatarFailed'));
    } finally { setUploading(false); }
  };

  return (
    <div className="flex w-full flex-col gap-6 md:max-w-2xl">
      {/* Perfil */}
      <Card>
        <CardHeader><CardTitle>{t('settings.profile')}</CardTitle><CardDescription>{t('settings.profileDesc')}</CardDescription></CardHeader>
        <CardContent className="flex flex-col gap-5">
          <div className="flex items-center gap-4">
            <div className="relative">
              <InitialsAvatar name={me?.name} email={me?.email} src={me?.avatar_url} size={72} />
              {uploading && <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40"><Spinner className="border-white/40 border-t-white" /></div>}
            </div>
            <div>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <Camera className="size-4" /> {t('settings.changeAvatar')}
              </Button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPickFile} />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="pname">{t('auth.name')}</Label>
              <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('auth.namePlaceholder')} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="pjob">{t('auth.jobTitle')}</Label>
              <Input id="pjob" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder={t('auth.jobPlaceholder')} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label>{t('auth.phone')}</Label>
            <PhoneInput value={me?.phone} onChange={setPhone} />
          </div>

          <div>
            <Button onClick={save} disabled={busy}>
              {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />}
              {t('settings.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Conta (somente leitura) */}
      <Card>
        <CardHeader><CardTitle>{t('settings.account')}</CardTitle><CardDescription>{t('settings.accountDesc')}</CardDescription></CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          {isLoading ? <Skeleton className="h-20 w-full" /> : (
            <>
              <Row label={t('settings.email')} value={me?.email ?? '—'} />
              <Row label={t('settings.role')} value={me?.role ?? '—'} />
              {/* Empresa (nome) em vez do UUID do tenant — editável na Conta (admin). */}
              <div className="flex items-center justify-between gap-4 border-b pb-3 last:border-0 last:pb-0">
                <span className="text-muted-foreground">{t('settings.company')}</span>
                <span className="flex items-center gap-2 text-right font-medium">
                  {me?.tenant_name || '—'}
                  {isAdmin && <Link to="/account" className="shrink-0 text-xs font-normal text-primary underline underline-offset-2">{t('common.edit')}</Link>}
                </span>
              </div>
              <Row label={t('settings.apiBase')} value={apiBase()} mono />
            </>
          )}
        </CardContent>
      </Card>

      {/* Aparência */}
      {/* Avisos de integração: e-mail, push do navegador e WhatsApp (com opt-in). */}
      <NotificationPrefs />

      <Card>
        <CardHeader><CardTitle>{t('settings.appearance')}</CardTitle><CardDescription>{t('settings.appearanceDesc')}</CardDescription></CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}>
            {resolvedTheme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            {resolvedTheme === 'dark' ? t('settings.toLight') : t('settings.toDark')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b pb-3 last:border-0 last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-xs' : 'font-medium'}>{value}</span>
    </div>
  );
}
